import React, { ComponentProps, ComponentRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RUN_CODE_LENGTH_LIMIT } from '@yandex-int/k-common';
import { Code } from '@edu-frontend/modules/src/shared/ui/Code';
import cn from 'classnames';
import { EditorView } from '@codemirror/view';
import { LESSON_HEADER_HEIGHT } from '@edu-frontend/modules/src/features/lessonHeaderMobile';
import { Extension } from '@codemirror/state';
import { useCodingStore } from '../../../state';
import { useAppContext } from '../../../../app-factory/context';

// @ts-ignore
import styles from './code-editor_view_mobile.module.scss';

interface CodingEditorMobileProps {
    initialValue: string;
    onChange?: ComponentProps<typeof Code>['onChange'];
    readOnly?: boolean;
    extensions: Extension[];
    initialState: ComponentProps<typeof Code>['initialState'];
}

export function CodeEditorMobile({
    initialValue,
    readOnly,
    onChange,
    extensions,
    initialState,
}: CodingEditorMobileProps) {
    const mobileEditorRef = useRef<ComponentRef<typeof Code> | null>(null);
    const isFocusOnInput = useCodingStore((store) => store.isFocusOnInput);
    const setIsFocusOnInput = useCodingStore((store) => store.setIsFocusOnInput);

    const { uatraits } = useAppContext();
    const isIOS = uatraits.OSFamily === 'iOS';
    const isTouch = uatraits.isTouch;

    const handleFocusChange = useCallback(
        (isFocused: boolean) => {
            setIsFocusOnInput(isFocused);
        },
        [setIsFocusOnInput]
    );

    const blurEditorFocus = useCallback(() => {
        mobileEditorRef?.current?.view?.contentDOM.blur();

        if (window) {
            window.removeEventListener('touchmove', blurEditorFocus);
        }
    }, []);

    useEffect(() => {
        const touchmoveHandler = () => {
            const selection = window.getSelection()?.toString();

            if (!selection) {
                blurEditorFocus();
            }
        };

        const copyHandler = () => {
            // На iOS программно убираем выделение после копирования,
            // т.к. там после копирования выделение сохраняется
            // и в таком случае ученику придется вручную удалять выделение
            // чтобы touchmove-ом скрыть клавиатуру и вернуть навигацию
            // (на android это поведение по умолчанию)
            if (isIOS) {
                window.getSelection()?.removeAllRanges();
            }
        };

        if (isFocusOnInput && window) {
            window.addEventListener('touchmove', touchmoveHandler);
            window.addEventListener('copy', copyHandler);
        }

        return isFocusOnInput && window
            ? () => {
                  window.removeEventListener('touchmove', touchmoveHandler);
                  window.removeEventListener('copy', copyHandler);
              }
            : undefined;
    }, [blurEditorFocus, isFocusOnInput, isIOS]);

    const additionalThemeSettings = useMemo(() => {
        return {
            dark: {
                // фон компилятора
                background: 'var(--hr-color-special-black)',
                // цвет текста
                foreground: '#eff',
                // цвет каретки
                caret: 'rgba(255, 255, 255, 0.9)',
                // цвет подсветки линии (#363e45 (--hr-color-text-primary) в rgb, плюс прозрачность)
                lineHighlight: !readOnly ? 'rgba(54, 62, 69, 0.5)' : 'transparent',
                // цвет выделения мышкой (#363e45 (--hr-color-text-primary) в rgb, плюс прозрачность)
                selection: 'rgba(183, 183, 183, 0.35)',
                // цвет фона столбца с номерами строк
                gutterBackground: 'var(--hr-color-special-black)',
                // цвет номеров строк
                gutterForeground: '#979EA6',
                // цвет номера активной строки
                gutterActiveForeground: '#979EA6',
            },
        };
    }, [readOnly]);

    const scrollToActiveLine = useCallback(() => {
        if (mobileEditorRef?.current) {
            const editorView = mobileEditorRef.current.view;
            editorView?.dispatch({
                effects: EditorView.scrollIntoView(editorView.state.selection.main.head, {
                    y: 'center',
                }),
            });
        }
    }, []);

    const [viewportHeight, setViewportHeight] = useState(window?.visualViewport?.height || 0);

    useEffect(() => {
        const handler = () => {
            if (window?.visualViewport) {
                setViewportHeight(window.visualViewport.height);
            }
        };
        window?.visualViewport?.addEventListener('resize', handler);

        return () => {
            window?.visualViewport?.removeEventListener('resize', handler);
        };
    }, []);

    useEffect(() => {
        // Когда фокусируемся на инпуте, скроллим к активной строке
        // Нужно чтобы активная строка не улетела за пределы экрана и подскролл произошел корректно, т.к. вьюпорт становится уже,
        // когда открывается мобильная клавиатура
        if (isFocusOnInput) {
            window.visualViewport?.addEventListener('resize', scrollToActiveLine);
        }

        return () => {
            window.visualViewport?.removeEventListener('resize', scrollToActiveLine);
        };
    }, [isFocusOnInput, scrollToActiveLine]);

    return (
        <div
            className={cn(styles.codeWrapper, isFocusOnInput && styles.codeWrapper_focused)}
            style={{ ['--code-editor-height']: `calc(${viewportHeight}px - ${LESSON_HEADER_HEIGHT})` }}
        >
            <Code
                additionalThemeSettings={additionalThemeSettings}
                className={styles.code}
                editable={!readOnly}
                extensions={extensions}
                initialState={initialState}
                maxLength={RUN_CODE_LENGTH_LIMIT}
                onBlur={isTouch ? () => handleFocusChange(false) : undefined}
                onChange={(value, update) => {
                    scrollToActiveLine();
                    onChange?.(value, update);
                }}
                onFocus={
                    isTouch
                        ? () => {
                              handleFocusChange(true);
                          }
                        : undefined
                }
                options={{
                    foldGutter: false,
                    allowMultipleSelections: true,
                    lineNumbers: true,
                    autocompletion: false,
                    // History extension is provided separately to accept custom params
                    history: false,
                }}
                ref={mobileEditorRef}
                styleSettings={{
                    activeLineGutterBackgroundColor: !readOnly ? '#4a535a' : 'transparent',
                    fontSize: 'var(--hr-typography-font-size-m)',
                    lineHeight: 'var(--hr-typography-line-height-m)',
                    padding: isFocusOnInput ? '16px 0' : '16px 0 80px',
                }}
                theme={'dark'}
                value={initialValue}
                withSyntaxHighlight
            />
        </div>
    );
}
